import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { animated, useSpring, useTransition } from 'react-spring';
import { styled } from 'styled-components';
import { color, layout, space, typography } from 'styled-system';
import { useMeasure } from '@hivebrite/react-hooks';
import { onEnterPress } from '@hivebrite/utils';
import { Box } from "../../box/src";
import { CATEGORIES, FontAwesomeIcon } from "../../font-awesome-icon/src";
import { __ } from "../../i18n/src";
import { Loader } from "../../loader/src";
import { ScreenReadersContent } from "../../screen-readers-content/src";
import { buttonVariant, focusOutline, font, ignoredProps, primaryShadow, useTheme, } from "../../theme/src";
import { sizeByKeyname, sizeByLetter } from './constants';
import { getButtonDisabledStyle, getButtonHoveredStyle, getButtonPressedStyle, getButtonStyle, } from './utils';
const CloseIcon = () => (_jsx(FontAwesomeIcon, { height: "20px", iconName: "xmark", iconType: CATEGORIES.SOLID }));
export const ButtonClose = forwardRef((_a, ref) => {
    var { screenReadersText } = _a, props = __rest(_a, ["screenReadersText"]);
    return (_jsx(Button, Object.assign({ "aria-label": screenReadersText ? undefined : __('Close'), icon: CloseIcon, ref: ref, screenReadersText: screenReadersText, size: "small", variant: "secondary" }, props)));
});
const ImageIcon = () => (_jsx(FontAwesomeIcon, { height: "14px", iconName: "image", iconType: CATEGORIES.SOLID }));
export const ButtonEditImage = forwardRef((_a, ref) => {
    var { screenReadersText } = _a, props = __rest(_a, ["screenReadersText"]);
    return (_jsx(Button, Object.assign({ "aria-label": screenReadersText ? undefined : __('Edit'), icon: ImageIcon, ref: ref, screenReadersText: screenReadersText, size: "small", variant: "inversed" }, props)));
});
const Component = (_a, ref) => {
    var { as = 'button', builderProps, children, dataTestId, icon: IconComponent, iconSpringStyles, isDisabled = false, isFullWidth = false, isLoading = false, onAnimateToDefault, onAnimateToDisabled, onAnimateToHovered, onAnimateToPressed, onClick, onKeyDown, onKeyPress, onKeyUp, screenReadersText, size = 'normal', truncate = true, type = 'button', variant = 'primary' } = _a, props = __rest(_a, ["as", "builderProps", "children", "dataTestId", "icon", "iconSpringStyles", "isDisabled", "isFullWidth", "isLoading", "onAnimateToDefault", "onAnimateToDisabled", "onAnimateToHovered", "onAnimateToPressed", "onClick", "onKeyDown", "onKeyPress", "onKeyUp", "screenReadersText", "size", "truncate", "type", "variant"]);
    const buttonSize = Object.assign(Object.assign({}, sizeByKeyname), sizeByLetter)[size];
    const hasChildren = Boolean(children);
    const buttonWidth = hasChildren
        ? isFullWidth
            ? '100%'
            : undefined
        : buttonSize;
    const immediateAnimation = useRef(true);
    const isInversed = variant === 'inversed';
    const [measureRef, { target }] = useMeasure();
    const theme = useTheme();
    const themeButton = theme.buttons[variant];
    const styles = useMemo(() => {
        const styleEntries = Object.entries({
            default: getButtonStyle,
            disabled: getButtonDisabledStyle,
            hovered: getButtonHoveredStyle,
            pressed: getButtonPressedStyle,
        }).map(([name, fn]) => [
            name,
            fn({
                builderProps,
                hasChildren,
                size,
                theme,
                themeButton,
            }),
        ]);
        return Object.fromEntries(styleEntries);
    }, [builderProps, hasChildren, size, theme, themeButton]);
    const [wrapStyle, springApi] = useSpring(() => (Object.assign({ immediate: immediateAnimation.current }, styles.default)));
    const handleBlur = useCallback(() => {
        if (isDisabled) {
            springApi.start(Object.assign({ immediate: immediateAnimation.current }, styles.disabled));
            onAnimateToDisabled === null || onAnimateToDisabled === void 0 ? void 0 : onAnimateToDisabled();
        }
        else {
            springApi.start(Object.assign({ immediate: immediateAnimation.current }, styles.default));
            onAnimateToDefault === null || onAnimateToDefault === void 0 ? void 0 : onAnimateToDefault();
        }
    }, [isDisabled, onAnimateToDefault, onAnimateToDisabled, springApi, styles]);
    const handleHover = useCallback(() => {
        springApi.start(Object.assign({ immediate: immediateAnimation.current }, styles.hovered));
        onAnimateToHovered === null || onAnimateToHovered === void 0 ? void 0 : onAnimateToHovered();
    }, [onAnimateToHovered, springApi, styles]);
    const handlePress = useCallback(() => {
        springApi.start(Object.assign({ immediate: immediateAnimation.current }, styles.pressed));
        onAnimateToPressed === null || onAnimateToPressed === void 0 ? void 0 : onAnimateToPressed();
    }, [onAnimateToPressed, springApi, styles]);
    const handleKeyDown = useCallback((e) => {
        onEnterPress(handlePress);
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    }, [handlePress, onKeyDown]);
    const handleKeyPress = useCallback((e) => {
        onEnterPress(onClick);
        onKeyPress === null || onKeyPress === void 0 ? void 0 : onKeyPress(e);
    }, [onClick, onKeyPress]);
    const handleKeyUp = useCallback((e) => {
        onEnterPress(handleHover);
        onKeyUp === null || onKeyUp === void 0 ? void 0 : onKeyUp(e);
    }, [handleHover, onKeyUp]);
    const iconProps = Object.assign(Object.assign({}, (IconComponent && {
        height: buttonSize - theme.space[2],
        size: buttonSize - theme.space[2],
        style: { maxWidth: 'unset' },
        width: buttonSize - theme.space[2],
    })), (iconSpringStyles && { springStyles: iconSpringStyles }));
    const loadStyle = useSpring({
        immediate: immediateAnimation.current,
        opacity: isLoading ? 0 : 1,
        x: isLoading ? -100 : 0,
    });
    const loadTransitions = useTransition(isLoading, {
        enter: { immediate: immediateAnimation.current, x: 0 },
        from: { immediate: immediateAnimation.current, x: 100 },
        leave: { immediate: immediateAnimation.current, x: 100 },
    });
    useEffect(() => {
        handleBlur();
    }, [handleBlur]);
    useEffect(() => {
        immediateAnimation.current = false;
    }, []);
    return (_jsxs(Wrapper, Object.assign({}, props, (!builderProps && { minHeight: buttonSize }), { "$isInversed": isInversed, as: typeof as === 'string' ? animated[as] : as, "data-testid": dataTestId, disabled: isLoading || isDisabled, onBlur: handleBlur, onClick: onClick, onFocus: handleHover, onKeyDown: handleKeyDown, onKeyPress: handleKeyPress, onKeyUp: handleKeyUp, onMouseDown: handlePress, onMouseEnter: handleHover, onMouseLeave: handleBlur, onMouseUp: handleHover, ref: ref, size: size, style: wrapStyle, type: as === 'button' ? type : undefined, variant: variant, width: buttonWidth, children: [loadTransitions(({ x }, item) => item && (_jsx(LoaderWrapper, { style: { transform: x.to((y) => `translate3d(0, ${y}%, 0)`) }, children: _jsx(Loader, { size: 16, strokeWidth: 2 }) }))), _jsxs(InnerWrapper, { "$truncate": truncate, ref: measureRef, style: {
                    opacity: loadStyle.opacity,
                    transform: loadStyle.x.to((y) => `translate3d(0, ${y}%, 0)`),
                }, children: [IconComponent && (_jsx(Box, { mr: hasChildren ? 2 : undefined, style: { color: 'inherit' }, children: _jsx(IconComponent, Object.assign({}, iconProps)) })), truncate && target && target.scrollWidth >= target.clientWidth ? (_jsx(TextEllipsis, { "$disabled": isDisabled, title: screenReadersText, children: children })) : (children), screenReadersText && (_jsx(ScreenReadersContent, { children: screenReadersText }))] })] })));
};
export const Button = forwardRef(Component);
const InnerWrapper = styled(animated.div) `
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  min-width: 0;
  ${({ $truncate }) => $truncate ? 'white-space: nowrap;' : 'word-break: break-word;'}
`;
const LoaderWrapper = styled(animated.div) `
  inset: 0;
  position: absolute;
`;
const TextEllipsis = styled.span `
  cursor: ${({ $disabled }) => $disabled ? 'default' : 'pointer'};
  overflow: hidden;
  pointer-events: auto;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Wrapper = styled.button
    .withConfig({
    shouldForwardProp: (prop) => !ignoredProps.includes(prop),
})
    .attrs({
    display: 'inline-block',
    overflow: 'hidden',
}) `
  ${buttonVariant}
  ${color}
  ${focusOutline}
  ${font}
  ${layout}
  ${space}
  ${typography}

  appearance: none;
  ${({ $isInversed }) => $isInversed ? primaryShadow : undefined}
  border-style: solid;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  ${({ disabled }) => (disabled ? 'pointer-events: none;' : undefined)}
  position: relative;
  user-select: none;
`;
//# sourceMappingURL=Button.js.map